import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Eye, EyeOff } from 'lucide-react';
import { userService } from '../../../_services';

function ChangePasswordDialog({ open, onOpenChange, repid }) {
    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [userId, setUserId] = useState('');
    const [userName, setUserName] = useState('');

    useEffect(() => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [id, name] = Usermeta_Data.split(':');
        setUserId(id);
        setUserName(name);
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error for this field
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation
        const newErrors = {
            newPassword: '',
            confirmPassword: ''
        };

        if (formData.newPassword.length < 6) {
            newErrors.newPassword = 'At least 6 characters in length';
        }

        if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Password do not match';
        }

        // Check if there are any errors
        if (newErrors.newPassword || newErrors.confirmPassword) {
            setErrors(newErrors);
            return;
        }

        // Update password
        setLoading(true);
        try {
            const apidata = await userService.updateuserpassword(
                userId,
                userName,
                repid,
                formData.newPassword
            );

            if (apidata && apidata.length > 0) {
                const result = apidata[0];
                
                if (result.code === "1") {
                    // Success - close dialog
                    onOpenChange(false);
                    // Reset form
                    setFormData({ newPassword: '', confirmPassword: '' });
                    setErrors({ newPassword: '', confirmPassword: '' });
                } else if (result.code === "-1" || result.code === "-2") {
                    // Error from API
                    setErrors(prev => ({ ...prev, newPassword: result.message }));
                }
            }
        } catch (error) {
            console.error('Error updating password:', error);
            setErrors(prev => ({ ...prev, newPassword: 'An error occurred. Please try again.' }));
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        // Reset form when closing
        setFormData({ newPassword: '', confirmPassword: '' });
        setErrors({ newPassword: '', confirmPassword: '' });
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Change Password</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <p className="text-sm text-gray-600">Your new password must:</p>
                            <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                                <li>Be at least 6 characters in length</li>
                                <li>Not be same as your current password</li>
                            </ul>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="newPassword">New Password</Label>
                            <div className="relative">
                                <Input
                                    id="newPassword"
                                    name="newPassword"
                                    type={showNewPassword ? 'text' : 'password'}
                                    placeholder=" "
                                    value={formData.newPassword}
                                    onChange={handleInputChange}
                                    onFocus={handleInputChange}
                                    className={errors.newPassword ? 'border-red-500' : ''}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                >
                                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            {errors.newPassword && (
                                <p className="text-sm text-red-500">{errors.newPassword}</p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Confirm Password</Label>
                            <div className="relative">
                                <Input
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    placeholder=" "
                                    value={formData.confirmPassword}
                                    onChange={handleInputChange}
                                    onFocus={handleInputChange}
                                    className={errors.confirmPassword ? 'border-red-500' : ''}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                >
                                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            {errors.confirmPassword && (
                                <p className="text-sm text-red-500">{errors.confirmPassword}</p>
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button 
                            type="button" 
                            variant="default" 
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="submit"
                            variant="secondary"
                            disabled={loading}
                        >
                            {loading ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default ChangePasswordDialog;
